import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, CircleDot } from 'lucide-react';
import { useData } from '../../context/DataContext';
import { formatoEuros, formatoPorcentaje } from '../../utils/numeros';
import { EstadoLimite, filasPresupuestos } from '../../utils/informes';
import { Tarjeta, Vacio, VistaInforme } from './comun';

const ESTADO: Record<EstadoLimite, { texto: string; icono: React.ReactNode; barra: string; cifra: string }> = {
  bien: {
    texto: 'Van bien',
    icono: <CheckCircle2 className="w-4 h-4 text-green-600 dark:text-green-400" />,
    barra: 'bg-blue-500',
    cifra: 'text-gray-900 dark:text-white',
  },
  cerca: {
    texto: 'Cerca del límite',
    icono: <CircleDot className="w-4 h-4 text-amber-600 dark:text-amber-400" />,
    barra: 'bg-amber-500',
    cifra: 'text-amber-700 dark:text-amber-300',
  },
  pasado: {
    texto: 'Pasados',
    icono: <AlertTriangle className="w-4 h-4 text-red-600 dark:text-red-400" />,
    barra: 'bg-red-500',
    cifra: 'text-red-600 dark:text-red-400',
  },
};

/** I3: cada presupuesto frente a lo gastado, con la previsión a fin de mes o de año. */
export const SeccionPresupuestos: React.FC<{ vista: VistaInforme }> = ({ vista }) => {
  const { presupuestos, transacciones, categorias } = useData();
  const filas = useMemo(
    () => filasPresupuestos(presupuestos, transacciones, vista.anio, vista.mes),
    [presupuestos, transacciones, vista.anio, vista.mes],
  );
  const cuenta = (e: EstadoLimite) => filas.filter(f => f.estado === e).length;
  const categoria = (id: string) => categorias.find(c => c.id === id);
  const hayAhorro = presupuestos.some(p => p.esObjetivoAhorro);

  if (filas.length === 0) {
    return (
      <Tarjeta titulo="Presupuestos">
        <Vacio>
          No hay presupuestos de gasto en este periodo.{' '}
          <Link to="/finanzas/presupuestos" className="text-blue-600 dark:text-blue-400 font-semibold hover:underline">Crear uno</Link>
        </Vacio>
      </Tarjeta>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        {(['bien', 'cerca', 'pasado'] as EstadoLimite[]).map(e => (
          <div key={e} className="bg-white dark:bg-gray-800 rounded-xl shadow p-3 text-center">
            <div className="flex items-center justify-center gap-1.5">{ESTADO[e].icono}
              <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">{ESTADO[e].texto}</span>
            </div>
            <p className={`text-2xl font-bold mt-1 ${ESTADO[e].cifra}`}>{cuenta(e)}</p>
          </div>
        ))}
      </div>

      <Tarjeta titulo="Cada presupuesto" subtitulo="Los anuales se miden con todo el año, también en la vista mensual.">
        <ul className="divide-y divide-gray-100 dark:divide-gray-700">
          {filas.map(f => {
            const cat = categoria(f.presupuesto.categoriaId);
            const pasara = f.prevision !== null && f.prevision > f.limite + 0.005;
            return (
              <li key={f.presupuesto.id} className="py-3">
                <div className="flex items-center gap-2">
                  <span className="text-base shrink-0">{cat?.icono ?? '📋'}</span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{f.presupuesto.nombre}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                      {cat?.nombre ?? 'Sin categoría'} · {f.presupuesto.periodicidad === 'anual' ? 'anual' : f.ambito === 'anio' ? 'mensual, suma del año' : 'mensual'}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className={`text-sm font-semibold tabular-nums ${ESTADO[f.estado].cifra}`}>{formatoEuros(f.gastado)}</p>
                    <p className="text-xs text-gray-400 tabular-nums">de {formatoEuros(f.limite)}</p>
                  </div>
                </div>
                <div className="mt-2 pl-7">
                  <div className="bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div className={`h-2 rounded-full ${ESTADO[f.estado].barra}`} style={{ width: `${Math.min(f.porcentaje, 100)}%` }} />
                  </div>
                  <div className="flex justify-between gap-2 text-xs mt-1">
                    <span className="text-gray-500 dark:text-gray-400">{formatoPorcentaje(f.porcentaje)}</span>
                    <span className={f.estado === 'pasado' ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'}>
                      {f.estado === 'pasado'
                        ? `Pasado en ${formatoEuros(f.gastado - f.limite)}`
                        : `Quedan ${formatoEuros(f.limite - f.gastado)}`}
                    </span>
                  </div>
                  {f.prevision !== null && f.estado !== 'pasado' && (
                    <p className={`text-xs mt-1 ${pasara ? 'text-amber-700 dark:text-amber-300' : 'text-gray-500 dark:text-gray-400'}`}>
                      Al ritmo de ahora acabarás {f.ambito === 'mes' ? 'el mes' : 'el año'} en {formatoEuros(f.prevision)}
                      {pasara ? `: te pasarías en ${formatoEuros(f.prevision - f.limite)}.` : '.'}
                    </p>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
        {hayAhorro && (
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
            Los objetivos de ahorro se siguen en <Link to="/finanzas/presupuestos" className="underline">Presupuestos</Link>.
          </p>
        )}
      </Tarjeta>
    </div>
  );
};
